// @flow
import GeometryType from 'ol/geom/GeometryType';
import Point from 'ol/geom/Point';
import MultiPoint from 'ol/geom/MultiPoint';
import LineString from 'ol/geom/LineString';
import { Style, Fill, Stroke, Circle as CircleStyle, Text } from 'ol/style';
import type { FeatureType } from '../../ui/openlayers';

const activeStyle = new Style({
    stroke: new Stroke({
        color: '#296458',
        width: 2,
        lineDash: [0.5, 3]
    }),
    fill: new Fill({
        color: 'rgba(200, 0, 0, 0.2)'
    })
});

const inactiveStyle = new Style({
    stroke: new Stroke({
        color: '#ba9a40',
        width: 1,
        lineDash: [1, 3]
    }),
    fill: new Fill({
        color: 'rgba(255, 255, 255, 0.1)'
    })
});

const hiddenStyle = new Style({
    stroke: new Stroke({
        color: 'rgba(255, 255, 255, 0)'
    }),
    fill: new Fill({
        color: 'rgba(255, 255, 255, 0)'
    })
});

const defaultEditableConfig = {
    point: {
        color: 'white',
        radius: 7
    },
    showNumberLabels: true
};

export const makeEditableStyle = (config = defaultEditableConfig) => (style) => {
    return (feature: FeatureType) => {
        // return style;
        const geometry = feature.getGeometry();
        const type = geometry.getType();

        let editableStyle;
        if(type === GeometryType.POLYGON || type === GeometryType.LINE_STRING) {
            const isLine = type === GeometryType.LINE_STRING;
            const coordinates = isLine ? geometry.getCoordinates() : geometry.getCoordinates()[0];
            editableStyle = [
                new Style({
                    image: new CircleStyle({
                        radius: config.point.radius,
                        fill: new Fill({
                            color: config.point.color
                        })
                    }),
                    geometry: new MultiPoint(coordinates)
                })
            ];

            if(config.showNumberLabels) {
                const textColor = '#ffffff';
                const backgroundColor = 'rgba(149,221,255,0.5)';

                const adjustLength = isLine ? 0 : 1;
                for(let i = 0, length = coordinates.length - adjustLength; i < length; i += 1) {
                    const coordinate = coordinates[i];
                    editableStyle.push(
                        new Style({
                            geometry: new Point(coordinate),
                            text: new Text({
                                font: 'normal 0.9em sans-serif',
                                text: String(i),
                                fill: new Fill({color: textColor}),
                                // backgroundFill: new Fill({color: backgroundColor}),
                                backgroundStroke: new Stroke({
                                    color: backgroundColor,
                                    lineJoin: 'round',
                                    width: 15,
                                    miterLimit: 2
                                }),
                                offsetX: -15,
                                offsetY: -14,
                                padding: [-6, -1, -6, 0]
                            })
                        })
                    );
                }
            }
        }
        else if(type === GeometryType.CIRCLE) {
            const center = geometry.getCenter();
            const radius = geometry.getRadius();

            editableStyle = [
                new Style({
                    geometry: new Point(feature.getGeometry().getCenter()),
                    image: new CircleStyle({
                        radius: config.point.radius,
                        fill: new Fill({
                            color: config.point.color
                        })
                    })
                }),
                new Style({
                    image: new CircleStyle({
                        radius: config.point.radius,
                        fill: new Fill({
                            color: config.point.color
                        })
                    }),
                    geometry: new MultiPoint([
                        [center[0], center[1] + radius],
                        [center[0] + radius, center[1]],
                        [center[0], center[1] - radius],
                        [center[0] - radius, center[1]]
                    ])
                })
            ];
        }
        else {
            throw new Error(`Unsupported geometry type ${type}`);
        }

        return [
            ...(Array.isArray(style) ? style : (style ? [style] : []) ),
            ...editableStyle
        ];
    };
};

type Config = {
    showInactive: boolean
}
export const makeStyleFunction = (config: Config) => (feature: FeatureType) => {
    const active = feature.get('active');

    if(active) {
        return activeStyle;
    }
    else if(config.showInactive) {
        return inactiveStyle;
    }
    else {
        return hiddenStyle;
    }
};